import { createListenerMiddleware } from '@reduxjs/toolkit'

// Create the middleware instance
export const listenerMiddleware = createListenerMiddleware()

// Add a listener that saves the state to sessionStorage whenever it changes
listenerMiddleware.startListening({
  predicate: (action) => {
    // Only save state for specific actions that modify user data or sidebar state
    return [
      'user/fetchUserData/fulfilled',
      'user/setActiveProfile',
      'user/setTrainerProfile',
      'user/setClientProfile',
      'user/setDebugMode',
      'user/setOnboardingComplete',
      'sidebar/updateShow',
      'sidebar/updateUnfoldable',
    ].includes(action.type)
  },
  effect: (action, listenerApi) => {
    const state = listenerApi.getState()
    // Save relevant parts of the state to sessionStorage
    const stateToPersist = {
      user: {
        user: state.user.user,
        trainerProfile: state.user.trainerProfile,
        clientProfile: state.user.clientProfile,
        activeProfile: state.user.activeProfile,
        onboardingComplete: state.user.onboardingComplete,
        debugMode: state.user.debugMode,
      },
      sidebar: {
        show: state.sidebar.show,
        unfoldable: state.sidebar.unfoldable,
      },
    }
    sessionStorage.setItem('reduxState', JSON.stringify(stateToPersist))
  },
})

// Add a listener for clearUser action to clear sessionStorage
listenerMiddleware.startListening({
  predicate: (action) => action.type === 'user/clearUser',
  effect: (action, listenerApi) => {
    console.log('🗑️ Clearing sessionStorage due to user logout/clearUser action')
    // Clear the persisted Redux state from sessionStorage
    sessionStorage.removeItem('reduxState')

    // Also clear localStorage items that might contain user-specific data
    localStorage.removeItem('activeProfile')
    localStorage.removeItem('debugMode')

    // Clear any user-specific cached data
    // Note: We keep language-specific data (exercises, commandTypes, etc.) as they're not user-specific
  },
})

// Add a listener for exercises state changes with language support
listenerMiddleware.startListening({
  predicate: (action) => {
    // Save exercises for specific actions
    return [
      'exercises/fetchExercises/fulfilled',
      'exercises/searchExercises/fulfilled',
      'exercises/addSingleExercise',
      'exercises/fetchExerciseById/fulfilled',
    ].includes(action.type)
  },
  effect: (action, listenerApi) => {
    const state = listenerApi.getState()
    // Get current language from multiple sources
    const currentLanguage =
      window.i18n?.language || window.i18next?.language || document.documentElement.lang || 'en'

    // Save exercises to localStorage with language key
    const exercisesToPersist = {
      exercises: state.exercises.exercises,
      referenceData: state.exercises.referenceData,
      timestamp: Date.now(),
      language: currentLanguage,
    }

    const storageKey = `exercises_${currentLanguage}`
    localStorage.setItem(storageKey, JSON.stringify(exercisesToPersist))

    console.log(
      `💾 Saved ${state.exercises.exercises.length} exercises for language: ${currentLanguage}`,
      `(Action: ${action.type})`,
    )

    // Additional debugging - log the specific exercise IDs being saved
    const exerciseIds = state.exercises.exercises.map((ex) => ex.id)
    console.log(`💾 Exercise IDs saved:`, exerciseIds)

    // If this is a fetchExerciseById action, log the specific exercise
    if (action.type === 'exercises/fetchExerciseById/fulfilled') {
      console.log(
        `💾 Specifically saved exercise from fetchExerciseById:`,
        action.payload?.id,
        action.payload?.name,
      )
    }
  },
})

// Add a listener for command types state changes with language support
listenerMiddleware.startListening({
  predicate: (action) => {
    // Save command types for specific actions
    return [
      'commandTypes/fetchCommandTypes/fulfilled',
      'commandTypes/fetchCommandType/fulfilled',
    ].includes(action.type)
  },
  effect: (action, listenerApi) => {
    const state = listenerApi.getState()
    // Get current language from multiple sources, or from the command types state
    const currentLanguage =
      state.commandTypes.language ||
      window.i18n?.language ||
      window.i18next?.language ||
      document.documentElement.lang ||
      'en'

    // Save command types to localStorage with language key
    const commandTypesToPersist = {
      commandTypes: state.commandTypes.commandTypes,
      timestamp: Date.now(),
      language: currentLanguage,
    }

    const storageKey = `commandTypes_${currentLanguage}`
    localStorage.setItem(storageKey, JSON.stringify(commandTypesToPersist))

    console.log(
      `💾 Saved ${state.commandTypes.commandTypes.length} command types for language: ${currentLanguage}`,
      `(Action: ${action.type})`,
    )
  },
})

// Add a listener for progression rule templates state changes with language support
listenerMiddleware.startListening({
  predicate: (action) => {
    // Save progression rule templates for specific actions
    return ['progressionRuleTemplates/fetchTemplates/fulfilled'].includes(action.type)
  },
  effect: (action, listenerApi) => {
    const state = listenerApi.getState()
    // Get current language from multiple sources, or from the templates state
    const currentLanguage =
      state.progressionRuleTemplates.language ||
      window.i18n?.language ||
      window.i18next?.language ||
      document.documentElement.lang ||
      'en'

    // Save progression rule templates to localStorage with language key
    const templatesToPersist = {
      templates: state.progressionRuleTemplates.templates,
      timestamp: Date.now(),
      language: currentLanguage,
    }

    const storageKey = `progressionRuleTemplates_${currentLanguage}`
    localStorage.setItem(storageKey, JSON.stringify(templatesToPersist))

    console.log(
      `💾 Saved ${state.progressionRuleTemplates.templates.length} progression rule templates for language: ${currentLanguage}`,
      `(Action: ${action.type})`,
    )
  },
})

// Function to load persisted state
export const loadPersistedState = () => {
  try {
    const persistedState = sessionStorage.getItem('reduxState')
    return persistedState ? JSON.parse(persistedState) : undefined
  } catch (error) {
    console.error('Error loading persisted state:', error)
    return undefined
  }
}

// Function to load exercises for a specific language
export const loadExercisesByLanguage = (language = 'en') => {
  try {
    const storageKey = `exercises_${language}`
    const persistedExercises = localStorage.getItem(storageKey)

    if (persistedExercises) {
      const parsed = JSON.parse(persistedExercises)

      // Check if the data is not too old (24 hours)
      const isStale = Date.now() - (parsed.timestamp || 0) > 24 * 60 * 60 * 1000

      if (!isStale && parsed.language === language) {
        console.log(
          `📦 Loaded ${
            parsed.exercises?.length || 0
          } exercises from localStorage for language: ${language}`,
        )
        return {
          exercises: parsed.exercises || [],
          referenceData: parsed.referenceData || {},
        }
      } else {
        console.log(
          `⚠️ Stale or mismatched language data found for: ${language} (isStale: ${isStale}, parsedLang: ${parsed.language})`,
        )
        // Remove stale data
        localStorage.removeItem(storageKey)
      }
    }

    return null
  } catch (error) {
    console.error('Error loading exercises by language:', error)
    return null
  }
}

// Function to clear exercises for a specific language
export const clearExercisesByLanguage = (language = 'en') => {
  try {
    const storageKey = `exercises_${language}`
    localStorage.removeItem(storageKey)
    console.log(`Cleared exercises for language: ${language}`)
  } catch (error) {
    console.error('Error clearing exercises by language:', error)
  }
}

// Function to load command types for a specific language
export const loadCommandTypesByLanguage = (language = 'en') => {
  try {
    const storageKey = `commandTypes_${language}`
    const persistedCommandTypes = localStorage.getItem(storageKey)

    if (persistedCommandTypes) {
      const parsed = JSON.parse(persistedCommandTypes)

      // Check if the data is not too old (24 hours)
      const isStale = Date.now() - (parsed.timestamp || 0) > 24 * 60 * 60 * 1000

      if (!isStale && parsed.language === language) {
        console.log(
          `📦 Loaded ${
            parsed.commandTypes?.length || 0
          } command types from localStorage for language: ${language}`,
        )
        return {
          commandTypes: parsed.commandTypes || [],
          language: parsed.language,
        }
      } else {
        console.log(
          `⚠️ Stale or mismatched command types data found for: ${language} (isStale: ${isStale}, parsedLang: ${parsed.language})`,
        )
        // Remove stale data
        localStorage.removeItem(storageKey)
      }
    }

    return null
  } catch (error) {
    console.error('Error loading command types by language:', error)
    return null
  }
}

// Function to clear command types for a specific language
export const clearCommandTypesByLanguage = (language = 'en') => {
  try {
    const storageKey = `commandTypes_${language}`
    localStorage.removeItem(storageKey)
    console.log(`Cleared command types for language: ${language}`)
  } catch (error) {
    console.error('Error clearing command types by language:', error)
  }
}

// Function to load progression rule templates for a specific language
export const loadProgressionRuleTemplatesByLanguage = (language = 'en') => {
  try {
    const storageKey = `progressionRuleTemplates_${language}`
    const persistedTemplates = localStorage.getItem(storageKey)

    if (persistedTemplates) {
      const parsed = JSON.parse(persistedTemplates)

      // Check if the data is not too old (24 hours)
      const isStale = Date.now() - (parsed.timestamp || 0) > 24 * 60 * 60 * 1000

      if (!isStale && parsed.language === language) {
        console.log(
          `📦 Loaded ${
            parsed.templates?.length || 0
          } progression rule templates from localStorage for language: ${language}`,
        )
        return {
          templates: parsed.templates || [],
          language: parsed.language,
        }
      } else {
        console.log(
          `⚠️ Stale or mismatched progression rule templates data found for: ${language} (isStale: ${isStale}, parsedLang: ${parsed.language})`,
        )
        // Remove stale data
        localStorage.removeItem(storageKey)
      }
    }

    return null
  } catch (error) {
    console.error('Error loading progression rule templates by language:', error)
    return null
  }
}

// Function to clear progression rule templates for a specific language
export const clearProgressionRuleTemplatesByLanguage = (language = 'en') => {
  try {
    const storageKey = `progressionRuleTemplates_${language}`
    localStorage.removeItem(storageKey)
    console.log(`Cleared progression rule templates for language: ${language}`)
  } catch (error) {
    console.error('Error clearing progression rule templates by language:', error)
  }
}

// Function to manually clear all user-related storage (useful for testing/debugging)
export const clearAllUserStorage = () => {
  try {
    console.log('🗑️ Manually clearing all user-related storage')
    // Clear sessionStorage
    sessionStorage.removeItem('reduxState')

    // Clear localStorage user items
    localStorage.removeItem('activeProfile')
    localStorage.removeItem('debugMode')

    console.log('✅ All user-related storage cleared')
  } catch (error) {
    console.error('Error clearing user storage:', error)
  }
}
